/** Profil — read-only seznam karet (Firma, IBAN, Rahmenvertrag, Logout). */

import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { useAuth } from "@/auth/auth-provider";
import { supabase } from "@/lib/supabase";
import { authedFetch } from "@/lib/auth";
import { EDGE_FUNCTION_BASE } from "@/lib/supabase";
import { fmtDateLong } from "@/lib/format";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import { useState, type ReactNode } from "react";
import { AccountUsersCard } from "./account-users";

interface PartnerSelfRow {
  id: string;
  framework_agreement_signed_at: string | null;
  framework_agreement_valid_until: string | null;
  framework_agreement_doc_id: string | null;
  payment_terms_days: number | null;
  partner_status: string;
}

interface PersonRow {
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  ico: string | null;
  dic: string | null;
  address_street: string | null;
  address_city: string | null;
  address_psc: string | null;
}

async function fetchSelf(partnerId: string): Promise<{
  partner: PartnerSelfRow | null;
  person: PersonRow | null;
}> {
  const { data: partner } = await supabase
    .from("partners")
    .select(
      "id, person_id, framework_agreement_signed_at, framework_agreement_valid_until, " +
        "framework_agreement_doc_id, payment_terms_days, partner_status",
    )
    .eq("id", partnerId)
    .maybeSingle();
  if (!partner) return { partner: null, person: null };

  const { data: person } = await supabase
    .from("persons")
    .select(
      "company_name, first_name, last_name, email, phone, ico, dic, " +
        "address_street, address_city, address_psc",
    )
    .eq("id", (partner as unknown as { person_id: string }).person_id)
    .maybeSingle();

  return {
    partner: partner as unknown as PartnerSelfRow,
    person: (person as unknown as PersonRow) ?? null,
  };
}

interface PdfLinkResponse {
  url: string;
  file_name: string;
}

export function AccountRoute() {
  const { partner, signOut, user, partnerUser } = useAuth();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selfQ = useQuery({
    queryKey: queryKeys.partnerSelf,
    queryFn: () =>
      partner ? fetchSelf(partner.partner_id) : Promise.resolve({ partner: null, person: null }),
    enabled: !!partner?.partner_id,
  });

  const data = selfQ.data;
  const ptn = data?.partner;
  const psn = data?.person;

  const openRahmenvertrag = async () => {
    if (!ptn?.framework_agreement_doc_id) return;
    setOpening(true);
    setError(null);
    try {
      const resp = await authedFetch<PdfLinkResponse>(
        `${EDGE_FUNCTION_BASE}/partner-portal/pdf-link`,
        {
          method: "POST",
          body: JSON.stringify({ document_id: ptn.framework_agreement_doc_id }),
        },
      );
      window.open(resp.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div style={{ padding: "32px 36px", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 className="display" style={{ margin: 0, fontSize: 28 }}>
          {t.nav.profile}
        </h1>
      </div>

      {selfQ.isLoading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>{t.loading}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Firma">
            <Field label="Firmenname" value={psn?.company_name || "—"} />
            <Field
              label="Ansprechpartner"
              value={[psn?.first_name, psn?.last_name].filter(Boolean).join(" ") || "—"}
            />
            <Field label="E-Mail" value={user?.email || psn?.email || "—"} />
            <Field label="Telefon" value={psn?.phone || "—"} />
            <Field
              label="Adresse"
              value={
                <>
                  <div>{psn?.address_street || "—"}</div>
                  <div>{[psn?.address_psc, psn?.address_city].filter(Boolean).join(" ") || ""}</div>
                </>
              }
            />
          </Card>

          <Card title="Steuer-Daten">
            <Field label="USt-ID / DIČ" value={psn?.dic || "—"} />
            <Field label="IČO" value={psn?.ico || "—"} />
            <Field
              label="Zahlungsziel"
              value={ptn?.payment_terms_days != null ? `${ptn.payment_terms_days} Tage netto` : "—"}
            />
          </Card>

          <Card title="Rahmenvertrag">
            <Field
              label="Unterzeichnet am"
              value={
                ptn?.framework_agreement_signed_at
                  ? fmtDateLong(ptn.framework_agreement_signed_at)
                  : "—"
              }
            />
            <Field
              label="Gültig bis"
              value={
                ptn?.framework_agreement_valid_until
                  ? fmtDateLong(ptn.framework_agreement_valid_until)
                  : "unbefristet"
              }
            />
            <Field label="Status" value={ptn?.partner_status || "—"} />
            {ptn?.framework_agreement_doc_id && (
              <div style={{ marginTop: 14 }}>
                <Button
                  variant="secondary"
                  onClick={() => void openRahmenvertrag()}
                  disabled={opening}
                >
                  {opening ? (
                    <Ic.Loader size={14} className="animate-spin" />
                  ) : (
                    <Ic.File size={14} />
                  )}
                  Rahmenvertrag öffnen
                </Button>
              </div>
            )}
          </Card>

          {partnerUser?.role === "admin" && <AccountUsersCard />}

          {error && (
            <div
              role="alert"
              style={{
                padding: 10,
                fontSize: 12.5,
                color: "var(--red)",
                background: "var(--red-12)",
                border: "1px solid rgba(248,113,113,0.22)",
                borderRadius: "var(--r-md)",
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => void signOut()}>
              <Ic.Logout size={14} /> Abmelden
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      <div style={{ display: "grid", gap: 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          marginBottom: 4,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13.5, color: "var(--text)" }}>{value}</div>
    </div>
  );
}
